import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Search, Trash2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const TABS = [
  { id: "submitted",    label: "Pending" },
  { id: "under_review", label: "Under Review" },
  { id: "accepted",     label: "Accepted" },
  { id: "denied",       label: "Denied" },
  { id: "archived",     label: "Archived" },
];

/**
 * OWNER-ONLY page — review and act on staff applications.
 * Full applicant answers + private notes visible here; NEVER shown to admins.
 */
export default function OwnerApplications() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("submitted");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [positions, setPositions] = useState([]);
  const [positionFilter, setPositionFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [selected, setSelected] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || user.role !== "owner")) navigate("/");
  }, [loading, user, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/owner/applications", {
        params: {
          status: tab,
          position_id: positionFilter || undefined,
          search: search || undefined,
          sort,
          limit: 100,
        },
      });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    }
  };

  useEffect(() => {
    if (user?.role === "owner") {
      (async () => {
        try { const { data } = await api.get("/owner/positions"); setPositions(data.items || []); } catch {}
      })();
    }
  }, [user]);

  useEffect(() => {
    if (user?.role === "owner") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tab, positionFilter, sort]);

  const doSearch = (e) => { e.preventDefault(); load(); };

  const openDetail = (a) => { setSelected(a); setNotesDraft(a.owner_notes || ""); };
  const closeDetail = () => { setSelected(null); setNotesDraft(""); };

  const patch = async (fields) => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/owner/applications/${selected.id}`, fields);
      setSelected(data);
      toast.success("Application updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };
  const saveNotes = () => patch({ owner_notes: notesDraft });
  const setStatus = (status) => patch({ status });
  const removeApp = async () => {
    if (!selected) return;
    if (!confirm("Delete this application permanently?")) return;
    try {
      await api.delete(`/owner/applications/${selected.id}`);
      toast.success("Deleted");
      closeDetail();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading || !user) return <div className="p-8 text-zinc-500">Loading…</div>;
  if (user.role !== "owner") return <div className="p-8 text-red-400">Owner only.</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-16" data-testid="owner-applications-page">
      <SEO title="Staff Applications · Owner · Serverlistr" canonical="/owner/applications" noindex />
      <h1 className="font-heading text-3xl font-black">Staff applications</h1>
      <p className="mt-2 text-zinc-400 text-sm">Owner-only. Admins and staff cannot see this page.</p>

      <div className="mt-6 flex items-center gap-1 flex-wrap" data-testid="app-tabs">
        {TABS.map((t) => {
          const n = counts[t.id] ?? 0;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`h-9 px-3 rounded-lg text-sm font-semibold border transition-all ${active ? "bg-white/15 border-white/30 text-white" : "bg-white/5 border-white/10 text-zinc-400 hover:text-white"}`}>
              {t.label} <span className="opacity-60">({n})</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <form onSubmit={doSearch} className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="app-search"
            placeholder="Search by Discord username or ID…"
            className="w-full h-10 pl-10 pr-3 bg-[#12121A] border border-white/10 rounded-lg text-sm focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple" />
        </form>
        <select value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)} data-testid="position-filter"
          className="h-10 px-3 bg-[#12121A] border border-white/10 rounded-lg text-sm">
          <option value="">All positions</option>
          {positions.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select"
          className="h-10 px-3 bg-[#12121A] border border-white/10 rounded-lg text-sm">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#0A0A0F] overflow-hidden">
        {items.length === 0 ? (
          <div className="p-6 text-zinc-500 text-sm">No applications in this tab.</div>
        ) : items.map((a) => (
          <button key={a.id} onClick={() => openDetail(a)} data-testid="app-row" data-application-id={a.id}
            className="w-full text-left px-4 py-3 border-t border-white/5 first:border-t-0 hover:bg-white/[0.03] flex items-center gap-3">
            {a.user_avatar ? (
              <img src={a.user_avatar} alt="" className="w-9 h-9 rounded-full bg-[#12121A]" />
            ) : (<div className="w-9 h-9 rounded-full bg-white/5" />)}
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{a.discord_username}</div>
              <div className="text-xs text-zinc-500 truncate">{a.position_name} · Discord {a.discord_id || "—"} · {new Date(a.submitted_at).toLocaleString()}</div>
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={closeDetail}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-2xl bg-[#0A0A0F] border border-white/10 p-6" data-testid="app-detail">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-heading text-xl font-black">{selected.discord_username}</h2>
                <div className="mt-1 text-xs text-zinc-500">
                  {selected.position_name} · Discord ID {selected.discord_id || "—"} · Submitted {new Date(selected.submitted_at).toLocaleString()}
                </div>
                <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-widest border bg-white/5 border-white/10">{selected.status}</div>
              </div>
              <button onClick={closeDetail} className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>

            <div className="mt-5 space-y-4 max-h-[55vh] overflow-y-auto pr-2 text-sm">
              {Object.entries(selected.answers || {}).map(([k, v]) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-500">{k.replace(/_/g, " ")}</div>
                  <div className="mt-1 text-zinc-200 whitespace-pre-wrap break-words">{v || <span className="text-zinc-600">—</span>}</div>
                </div>
              ))}
            </div>

            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-500">Private owner notes</div>
              <textarea value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} rows={3} data-testid="notes-input"
                className="mt-1 w-full px-3 py-2 bg-[#12121A] border border-white/10 rounded-lg text-sm focus:border-blurple focus:outline-none resize-y" />
              <button onClick={saveNotes} disabled={busy} data-testid="save-notes-btn" className="mt-2 h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold">Save notes</button>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <ActionBtn onClick={() => setStatus("under_review")} testId="mark-review-btn" className="bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-300 border-yellow-500/40">Mark Under Review</ActionBtn>
              <ActionBtn onClick={() => setStatus("accepted")} testId="accept-btn" className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border-emerald-500/40"><Check className="w-4 h-4" /> Accept</ActionBtn>
              <ActionBtn onClick={() => setStatus("denied")} testId="deny-btn" className="bg-red-500/10 hover:bg-red-500/20 text-red-300 border-red-500/40"><X className="w-4 h-4" /> Deny</ActionBtn>
              <ActionBtn onClick={() => setStatus("archived")} testId="archive-btn" className="bg-white/5 hover:bg-white/10 text-zinc-300 border-white/10">Archive</ActionBtn>
              <ActionBtn onClick={removeApp} testId="delete-app-btn" className="bg-red-500/10 hover:bg-red-500/20 text-red-300 border-red-500/40"><Trash2 className="w-4 h-4" /> Delete</ActionBtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionBtn({ children, onClick, testId, className }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-semibold transition-all ${className}`}>
      {children}
    </button>
  );
}
